/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-nested-ternary */
import React, { FC, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/react-hooks';
import DataTable from '@salesforce/design-system-react/components/data-table';
import DataTableColumn from '@salesforce/design-system-react/components/data-table/column';
import DataTableCell from '@salesforce/design-system-react/components/data-table/cell';
import '../index.scss';
import OrderStatusCell from './OrderStatusCell';
import OrderIdCell from './OrderIdCell';
import EventCell from './EventCell';
import OrderRequestIdCell from './OrderRequestIdCell';
import OrderRequestStatusCell from './OrderRequestStatusCell';
import SubmissionStatusCell from './SubmissionStatusCell';
import SubmitDateCell from './SubmitDateCell';
import AccountCell from './AccountCell';
import Inventory3PL from './Inventory3PL';
import GET_SUBMISSION_STATUSES_BY_CASES from '../../../graphql/getSubmissionStatusesByCases';
import { shouldShowSubmissionInFlight } from '../../../util/submissionStatus';
import { INVENTORY_REQUEST_DETAIL_UI, INVENTORY_STATUS, LIMIT } from '../../../util/constants';

interface Props {
  type: string;
  orders: any[];
  getRequestsList: (list: any) => void;
  sortedcolumn: (property: any) => void;
  filters: any;
  offsetData?: any;
}

interface Sort {
  sortColumn: string;
  sortColumnDirection: {
    [key: string]: string;
  };
}

// CustomDataTableCell must have the same displayName as DataTableCell or it will be ignored.
OrderIdCell.displayName = DataTableCell.displayName;
OrderStatusCell.displayName = DataTableCell.displayName;
OrderRequestIdCell.displayName = DataTableCell.displayName;
OrderRequestStatusCell.displayName = DataTableCell.displayName;
SubmissionStatusCell.displayName = DataTableCell.displayName;
SubmitDateCell.displayName = DataTableCell.displayName;
AccountCell.displayName = DataTableCell.displayName;
EventCell.displayName = DataTableCell.displayName;
Inventory3PL.displayName = DataTableCell.displayName;

const OrdersDataTable: FC<Props> = ({
  orders,
  type,
  getRequestsList,
  sortedcolumn,
  filters,
  offsetData,
}) => {
  const [sortObj, setSortObj] = useState<Sort>();

  // Batched submission-status lookup for the Open tab: ONE query (plus one poll while any row is
  // in-flight) for all loaded cases, replacing the per-row getSubmissionStatusByCase query/poll
  // that SubmissionStatusCell used to run. Each row is enriched with its status record below.
  const openCaseExternalIds = useMemo(
    () =>
      type === 'Open'
        ? Array.from(new Set((orders || []).map((o: any) => o?.caseExternalId).filter(Boolean)))
        : [],
    [type, orders]
  );

  const [isPollingSubmissions, setIsPollingSubmissions] = useState(false);

  const { data: submissionStatusesData } = useQuery(GET_SUBMISSION_STATUSES_BY_CASES, {
    fetchPolicy: 'no-cache',
    skip: openCaseExternalIds.length === 0,
    pollInterval: isPollingSubmissions
      ? INVENTORY_REQUEST_DETAIL_UI.SUBMISSION_POLL_INTERVAL_MS
      : 0,
    variables: { caseExternalIds: openCaseExternalIds },
  });

  const submissionStatusByCase = useMemo(() => {
    const statusMap: Record<string, any> = {};
    (submissionStatusesData?.getSubmissionStatusesByCases || []).forEach((entry: any) => {
      if (entry?.caseExternalId) {
        statusMap[entry.caseExternalId] = entry;
      }
    });
    return statusMap;
  }, [submissionStatusesData]);

  useEffect(() => {
    if (openCaseExternalIds.length === 0) {
      setIsPollingSubmissions(false);
      return;
    }
    const anyInFlight = Object.values(submissionStatusByCase).some(record =>
      shouldShowSubmissionInFlight(
        record as any,
        INVENTORY_REQUEST_DETAIL_UI.SUBMISSION_RECOVERY_MAX_AGE_MS
      )
    );
    setIsPollingSubmissions(anyInFlight);
  }, [submissionStatusByCase, openCaseExternalIds.length]);

  const itemsWithSubmissionStatus = useMemo(
    () =>
      (orders || []).map((order: any) => ({
        ...order,
        submissionStatusRecord: order?.caseExternalId
          ? submissionStatusByCase[order.caseExternalId] ?? null
          : null,
      })),
    [orders, submissionStatusByCase]
  );

  const handleSort = (sortColumn: any): void => {
    const sortProperty = sortColumn.property;
    const { sortDirection } = sortColumn;
    const sort = {
      sortColumn: sortProperty,
      sortColumnDirection: {
        [sortProperty]: sortDirection,
      },
    };
    // needs to work in both directions
    setSortObj(sort);
    sortedcolumn(sortColumn);
    if (type === 'Open') {
      getRequestsList({
        variables: {
          limit: LIMIT,
          orderBy: sortProperty,
          orderSortType: sortDirection,
          offset: offsetData,
          type: INVENTORY_STATUS.REQUESTED.toUpperCase(),
          filters,
        },
      });
    } else if (type === 'Failed' || type === 'Processed') {
      getRequestsList({
        variables: {
          limit: LIMIT,
          orderBy: sortProperty,
          orderSortType: sortDirection,
          offset: offsetData,
          orderType: 'INVENTORY_REQUEST',
          screenType: type.toUpperCase(),
          filters,
        },
      });
    }
  };
  return (
    <DataTable
      assistiveText={{
        columnSort: 'sort this column',
        columnSortedAscending: 'asc',
        columnSortedDescending: 'desc',
      }}
      items={itemsWithSubmissionStatus}
      id="Inventory-Request-DataTable"
      className="font-12"
      fixedLayout
      keyboardNavigation
      joined
      onSort={handleSort}
    >
      {[
        type === 'Favorites' ? (
          <DataTableColumn
            key="eventId"
            label="Name"
            property="eventId"
            width="20%"
            isSorted={sortObj?.sortColumn === 'eventId'}
            sortable
            sortDirection={sortObj?.sortColumnDirection.eventId}
          >
            <OrderRequestIdCell type={type} />
          </DataTableColumn>
        ) : type === 'Open' ? (
          <DataTableColumn
            key="eventId"
            label="IR"
            property="eventId"
            width="10%"
            isSorted={sortObj?.sortColumn === 'eventId'}
            sortable
            sortDirection={sortObj?.sortColumnDirection.eventId}
          >
            <OrderRequestIdCell type={type} />
          </DataTableColumn>
        ) : (
          <DataTableColumn
            key="caseNumber"
            label="IR"
            property="caseNumber"
            width="10%"
            isSorted={sortObj?.sortColumn === 'caseNumber'}
            sortable
            sortDirection={sortObj?.sortColumnDirection.caseNumber}
          >
            <OrderRequestIdCell type={type} />
          </DataTableColumn>
        ),
        (type === 'Failed' || type === 'Processed' || type === 'Open') && (
          <DataTableColumn
            key="parentEventId"
            label="Event"
            property="parentEventId"
            width="10%"
            isSorted={sortObj?.sortColumn === 'parentEventId'}
            sortable
            sortDirection={sortObj?.sortColumnDirection.parentEventId}
          >
            <EventCell type={type} />
          </DataTableColumn>
        ),
        (type === 'Failed' || type === 'Processed') && (
          <DataTableColumn
            key="type"
            label="Order type"
            property="type"
            width="10%"
            isSorted={sortObj?.sortColumn === 'type'}
            sortable
            sortDirection={sortObj?.sortColumnDirection.type}
          />
        ),
        type === 'Open' && (
          <DataTableColumn
            key="eventStatus"
            label="Status"
            property="eventStatus"
            width="150px"
            isSorted={sortObj?.sortColumn === 'eventStatus'}
            sortable
            sortDirection={sortObj?.sortColumnDirection.eventStatus}
          >
            <SubmissionStatusCell type={type} />
          </DataTableColumn>
        ),
        (type === 'Failed' || type === 'Processed') && (
          <DataTableColumn
            key="status"
            label="Status"
            property="status"
            width="150px"
            isSorted={sortObj?.sortColumn === 'status'}
            sortable
            sortDirection={sortObj?.sortColumnDirection.status}
          >
            <SubmissionStatusCell type={type} />
          </DataTableColumn>
        ),
        type === 'Failed' && (
          <DataTableColumn
            key="failureReason"
            label="Failure Reason"
            property="failureReason"
            isSorted={sortObj?.sortColumn === 'failureReason'}
            sortable
            sortDirection={sortObj?.sortColumnDirection.failureReason}
          />
        ),
        type === 'Processed' && (
          <DataTableColumn
            key="erpOrderNumber"
            label="ERP Order"
            property="erpOrderNumber"
            width="85px"
            isSorted={sortObj?.sortColumn === 'erpOrderNumber'}
            sortable
            sortDirection={sortObj?.sortColumnDirection.erpOrderNumber}
          >
            <OrderIdCell type={type} />
          </DataTableColumn>
        ),
        (type === 'Open' || type === 'Processed' || type === 'Failed') && (
          <DataTableColumn
            key="accountName"
            label="Account"
            property="accountNumber"
            width="250px"
            isSorted={sortObj?.sortColumn === 'accountNumber'}
            sortable
            sortDirection={sortObj?.sortColumnDirection.accountName}
          >
            <AccountCell type={type} />
          </DataTableColumn>
        ),
        type === 'Open' ? (
          <DataTableColumn
            key="salesRep"
            label="Sales Rep"
            property="salesRep"
            isSorted={sortObj?.sortColumn === 'salesRep'}
            sortable
            sortDirection={sortObj?.sortColumnDirection.salesRep}
          />
        ) : (
          type !== 'Favorites' && (
            <DataTableColumn
              key="salesRepName"
              label="Sales Rep"
              property="salesRepName"
              isSorted={sortObj?.sortColumn === 'salesRepName'}
              sortable
              sortDirection={sortObj?.sortColumnDirection.salesRepName}
            />
          )
        ),
        type === 'Open' ? (
          <DataTableColumn
            key="territoryNumber"
            label="Territory"
            property="territoryNumber"
            isSorted={sortObj?.sortColumn === 'territoryNumber'}
            sortable
            sortDirection={sortObj?.sortColumnDirection.territoryNumber}
          />
        ) : (
          type !== 'Favorites' && (
            <DataTableColumn
              key="caseTerritoryName"
              label="Territory"
              property="caseTerritoryName"
              isSorted={sortObj?.sortColumn === 'caseTerritoryName'}
              sortable
              sortDirection={sortObj?.sortColumnDirection.caseTerritoryName}
            />
          )
        ),
        <DataTableColumn
          key="userHoldReasons"
          label="Hold Reason"
          property="userHoldReasons"
          isSorted={sortObj?.sortColumn === 'userHoldReasons'}
          sortable
          sortDirection={sortObj?.sortColumnDirection.userHoldReasons}
        />,
        (type === 'Open' || type === 'Processed' || type === 'Failed') && (
          <DataTableColumn
            key="3PLDelivery"
            label="3PL Delivery"
            property="3PLDelivery"
            isSorted={sortObj?.sortColumn === '3PLDelivery'}
            sortable
            sortDirection={sortObj?.sortColumnDirection.serviceLevel}
          >
            <Inventory3PL />
          </DataTableColumn>
        ),
        <DataTableColumn
          key="serviceLevel"
          label="Service Level"
          property="serviceLevel"
          isSorted={sortObj?.sortColumn === 'serviceLevel'}
          sortable
          sortDirection={sortObj?.sortColumnDirection.serviceLevel}
        />,
        type !== 'Favorites' && (
          <DataTableColumn
            isSorted={sortObj?.sortColumn === 'submittedDate'}
            key="submittedDate"
            label="Submit Date"
            property="submittedDate"
            sortable
            sortDirection={sortObj?.sortColumnDirection.submittedDate}
          >
            <SubmitDateCell />
          </DataTableColumn>
        ),
        // type === 'Failed' && (
        //   <DataTableColumn key="action" label="Action" property="action">
        //     <OrderIdCell actionButton type={type} />
        //   </DataTableColumn>
        // ),
        type === 'Favorites' && (
          <DataTableColumn
            isSorted={sortObj?.sortColumn === 'favComments'}
            key="favComments"
            label="Comments"
            property="favComments"
            sortable
            sortDirection={sortObj?.sortColumnDirection.favComments}
          />
        ),
      ]}
    </DataTable>
  );
};

export default OrdersDataTable;
